import { Injectable } from '@angular/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { blobTo64 } from '../../utils/utils';

@Injectable({
  providedIn: 'root'
})
export class FileService {

  constructor() { }

  /**
   * Vérifie si un fichier existe dans un répertoire donné.
   */
  async fileExists(path: string, directory: Directory = Directory.Documents): Promise<boolean> {
    try {
      await Filesystem.stat({ path, directory });
      return true;
    } catch (err: any) {
      if (err.message?.includes('does not exist') || err.message?.includes('File does not exist')) {
        return false;
      }
      throw err;
    }
  }

  /**
   * Lit un fichier s'il existe, sinon lève une erreur explicite.
   */
  async readFile(path: string, directory: Directory = Directory.Documents) {
    const exists = await this.fileExists(path, directory);
    if (!exists) throw new Error(`File "${path}" does not exist`);
    return Filesystem.readFile({ path, directory });
  }

  /**
   * Enregistre un fichier (blob) à un chemin donné.
   */
  saveFile(blobToSave: Blob, toPath: string, directory: Directory): Promise<string> {
    const splittedToPath = toPath.split('/');
    const pathWithoutFileName = splittedToPath.slice(0, -1).join('/');
    const fileName = splittedToPath[splittedToPath.length - 1];

    return new Promise(async (resolve, reject) => {
      try {
        // ✅ Vérifie que le blob n'est pas vide
        if (!blobToSave || blobToSave.size === 0) {
          throw new Error('❌ Blob is empty — aborting writeFile.');
        }

        const dataAsString = await blobTo64(blobToSave);

        // ✅ Vérifie que la chaîne base64 est bien remplie
        const base64Content = dataAsString.split(',')[1];
        if (!base64Content || base64Content.trim().length === 0) {
          throw new Error('❌ Blob content is empty — writeFile skipped.');
        }

        // mkdir si besoin
        if (pathWithoutFileName) {
          await Filesystem.mkdir({
            path: pathWithoutFileName,
            directory,
            recursive: true
          }).catch(() => {});
        }

        await Filesystem.writeFile({
          path: toPath,
          data: dataAsString,
          directory
        });

        resolve(fileName);
      } catch (err) {
        console.error('saveFile error:', err);
        reject(fileName);
      }
    });
  }

  /**
   * Écrit un fichier dans le cache (sans vérification).
   */
  copyInCache(data: string, path: string) {
    return Filesystem.writeFile({
      path,
      data,
      directory: Directory.Cache
    });
  }

}
